/**
 * Shared helper for GS "action" POSTs (write/decision endpoints) — CT-5095.
 *
 * Same envelope + error contract that `taskActions.js` (CT-5093) established:
 * success AND error share the GS envelope (HTTP 200; the real outcome is the
 * inner `statusCode` / `msgCode`). On success returns the unwrapped envelope; on
 * failure throws an Error carrying `.status` (internal statusCode) and `.code`
 * (msgCode) so callers can branch. A BARE HTTP error (no envelope) — the observed
 * beta/GS 500 — is normalized to the same Error shape via the interceptor's
 * `{ message, status }` rejection.
 */

import { extractApiError, validateApiResponse } from '../shared/utils/apiResponse.js';
import { gsApi } from './apiClient.js';

/** Build a normalized action Error carrying the internal status + msgCode. */
export function gsActionError(message, status, code, fallbackMessage) {
  const error = new Error(message || fallbackMessage);
  if (status != null) error.status = status;
  if (code) error.code = code;
  return error;
}

/** First msgCode string from a GS envelope, or null. */
function firstMsgCode(envelope) {
  const codes = envelope?.msgCode ?? envelope?.msg_code ?? [];
  return codes[0]?.code ?? null;
}

/**
 * POST a GS action. Success → the unwrapped envelope (`{ body, meta, ... }`).
 * Failure → throws an Error with `.status` + `.code` (enveloped error, the
 * contract path) or `.status` only (bare HTTP error, the defensive fallback).
 *
 * @param {string} path GS path (e.g. `/v1/my-work/change-request/resolve`).
 * @param {Object} payload JSON body (camelCase — the my-work family).
 * @param {string} fallbackMessage user-facing fallback when the envelope has none.
 */
export async function postGsAction(path, payload, fallbackMessage) {
  let envelope;
  try {
    envelope = await gsApi.post(path, payload);
  } catch (rejected) {
    // Defensive fallback: a bare HTTP error with no GS envelope (e.g. the
    // change-request/resolve approve → 500 SYS001 observed 2026-09-03).
    throw gsActionError(rejected?.message, rejected?.status, null, fallbackMessage);
  }
  const apiError = extractApiError(envelope, fallbackMessage);
  if (apiError) {
    throw gsActionError(apiError.message, apiError.statusCode, firstMsgCode(envelope), fallbackMessage);
  }
  return validateApiResponse(envelope, fallbackMessage);
}
